import {
  ArenaGameState,
  CardGameType,
  SeatPosition,
  GamePhase
} from '../../state/ArenaTypes';
import { GameTableConfig } from '../../engine/tableConfig/types';
import { DEFAULT_TIMING } from '../../engine/tableConfig/defaults';
import {
  IGameModule,
  GameTerminology,
  GameLabels,
  GameIcons,
  GameDisplayIndicators,
  GamePresentationResult,
  BotTurnResolution,
  BotTurnTimingContext
} from '../types';
import { GamePresentation } from '../../presentation/GamePresentationTypes';
import { extractNormalizedSeatsList } from '../common/seatUtils';

// Presentation components
import { HeartsTableHud } from './presentation/HeartsTableHud';
import { HeartsCelebrationPill } from './presentation/HeartsCelebrationPill';
import { HeartsCelebrationOverlay } from './presentation/HeartsCelebrationOverlay';
import { HeartsPassingHud } from './presentation/HeartsPassingHud';
import { HeartsBrokenBanner } from '../../components/Table/HeartsBrokenBanner';
import { HeartsScorePresenter } from './HeartsScorePresenter';
import { HeartsContractFormatter } from './HeartsContractFormatter';
import { HeartsEventSynthesizer } from './events/HeartsEventSynthesizer';
import { StandardHandSortingStrategy } from '../common/StandardHandSortingStrategy';

export const HEARTS_PRESENTATION: GamePresentation = {
  Hud: HeartsTableHud,
  CelebrationPill: HeartsCelebrationPill,
  CelebrationOverlay: HeartsCelebrationOverlay,
  phases: {
    PASSING: HeartsPassingHud,
  },
  events: {
    HEARTS_BROKEN: HeartsBrokenBanner,
  },
};

/**
 * Hearts Game Module: Reference implementation of Client-Side Game Knowledge + Presentation.
 *
 * ARCHITECTURAL PRINCIPLE:
 * THE SERVER IS THE ONLY SOURCE OF TRUTH.
 *
 * The Hearts client module:
 *  - Understands Hearts terminology, labels, and icons/theme.
 *  - Selects Hearts-specific presentation components (HeartsStatusHud, HeartsPassingHud, HeartsBrokenBanner).
 *  - Provides phase-specific presentation and formats authoritative contract/outcome information.
 *  - Projects authoritative server state into display indicators WITHOUT calculating authoritative facts.
 *
 * The Hearts client module MUST NEVER:
 *  - Calculate authoritative penalties or scores.
 *  - Determine legal plays or trick winners.
 *  - Determine card ownership.
 *  - Determine authoritative game phases or outcomes.
 *  - Determine whether Hearts is legally broken or who took the Queen of Spades.
 *  - Determine whether a player actually shot the moon.
 *  - Independently recreate Hearts rules or mutate authoritative game state.
 *
 * If client and server ever disagree:
 * SERVER STATE WINS.
 */
export class HeartsGameModule implements IGameModule {
  public readonly gameType: CardGameType = 'HEARTS';
  public readonly displayName: string = 'Hearts';
  public readonly aliases: readonly string[] = [
    '100_PTS',
    '50_PTS',
    'HEART'
  ];
  public readonly scorePresenter = new HeartsScorePresenter();
  public readonly contractFormatter = new HeartsContractFormatter();
  public readonly handSortingStrategy = new StandardHandSortingStrategy();
  public readonly presentation: GamePresentation = HEARTS_PRESENTATION;
  public readonly eventSynthesizer = new HeartsEventSynthesizer();

  // Hearts Specific Features & Contracts
  public readonly supportsPassingPhase = true;
  public readonly isPenaltyScoring = true;
  public readonly defaultTrumpSuit = null;
  public readonly hasKitty = false;
  public readonly hasTrumpDeclaration = false;
  public readonly hasKittyDiscard = false;

  public getPhaseTimeoutSec(phase: GamePhase, config?: GameTableConfig | null): number {
    const timing = config?.timing ?? DEFAULT_TIMING;
    switch (phase) {
      case 'PASSING':
        return timing.passingTimeoutSec ?? 20;
      case 'PLAYING':
      default:
        return timing.turnTimeoutSec;
    }
  }

  public getBotDelayMs(phase: GamePhase, config?: GameTableConfig | null): number {
    const timing = config?.timing ?? DEFAULT_TIMING;
    switch (phase) {
      case 'PASSING':
      case 'PLAYING':
      default:
        return timing.botPlayDelayMs;
    }
  }

  public getTeamForSeat(_seat: SeatPosition): 1 | 2 | null {
    return null;
  }

  public getPartnerSeat(_seat: SeatPosition): SeatPosition | null {
    return null;
  }

  public getPlayerCount(): number {
    return 4;
  }

  public isPrePlayPhase(phase: GamePhase): boolean {
    return phase === 'LOBBY' || phase === 'DEALING' || phase === 'PASSING';
  }

  public isSetupPhase(phase: GamePhase): boolean {
    return phase === 'DEALING' || phase === 'PASSING';
  }

  public isActiveGameplayPhase(phase: GamePhase): boolean {
    return phase === 'PASSING' || phase === 'PLAYING';
  }

  public readonly terminology: GameTerminology = {
    trickNoun: 'trick',
    trickNounPlural: 'tricks',
    bidNoun: 'pass',
    trumpNoun: 'penalty suit',
    penaltyNoun: 'penalty points',
    sweepNoun: 'Shoot the Moon',
    partnerNoun: 'none'
  };

  public readonly labels: GameLabels = {
    displayName: 'Hearts',
    scoreboardTitle: 'Hearts Penalty Scoreboard',
    team1Label: 'Player 1',
    team2Label: 'Player 2',
    winningBidLabel: 'Passing Direction',
    contractSummaryLabel: 'Pass Direction',
    phaseDisplayNames: {
      LOBBY: 'Table Lobby',
      DEALING: 'Dealing 13 Cards',
      PASSING: 'Passing Cards',
      PLAYING: 'Playing Trick',
      TRICK_END: 'Trick Evaluation',
      TRICK_RESOLVED: 'Trick Complete',
      HAND_END: 'Hand Complete',
      GAME_OVER: 'Match Concluded'
    }
  };

  public readonly icons: GameIcons = {
    themeSymbol: '♥',
    themeColor: 'red',
    iconType: 'heart',
    statusBadges: {
      heartsBroken: '💔',
      queen: '👑',
      moon: '🌙'
    }
  };

  /**
   * Projects authoritative server state into presentation indicators.
   * Performs ZERO rule evaluation, card scanning, or score calculations.
   */
  public getDisplayIndicators(
    state: ArenaGameState,
    _config?: GameTableConfig | null
  ): GameDisplayIndicators {
    // Cumulative scores provided authoritatively by the server
    const rawScores = state.playerScores ?? (state as any).seatScores ?? [
      state.team1Score ?? 0,
      state.team2Score ?? 0,
      0,
      0
    ];
    const playerScores = Array.isArray(rawScores) ? rawScores : [0, 0, 0, 0];

    // Current hand penalty points provided authoritatively by server (seatPoints / pointsTaken)
    const rawSeatPoints = state.seatPoints ?? (state as any).pointsTaken ?? (state.gameExt as any)?.seatPoints ?? [0, 0, 0, 0];
    const seatRoundPenaltyPoints = Array.isArray(rawSeatPoints) ? rawSeatPoints : [0, 0, 0, 0];

    // Queen of Spades status provided authoritatively by server
    const queenOfSpadesPlayed = Boolean(
      state.queenOfSpadesPlayed ??
      (state.gameExt as any)?.queenOfSpadesPlayed ??
      (state.queenOfSpadesTakenBySeat !== undefined && state.queenOfSpadesTakenBySeat !== null)
    );

    const queenOfSpadesTakenBySeat = (state.queenOfSpadesTakenBySeat !== undefined && state.queenOfSpadesTakenBySeat !== null)
      ? (Number(state.queenOfSpadesTakenBySeat) as SeatPosition)
      : ((state.gameExt as any)?.queenOfSpadesTakenBySeat !== undefined && (state.gameExt as any)?.queenOfSpadesTakenBySeat !== null)
        ? (Number((state.gameExt as any).queenOfSpadesTakenBySeat) as SeatPosition)
        : null;

    // Moon threat presentation badge: derived purely visually from authoritative server data
    // If the server explicitly declared a moon shooter, use that seat; otherwise project visual alert if a seat has >= 15 pts.
    let moonThreatSeat: SeatPosition | null = null;
    const authoritativeMoonSeat = state.shotTheMoonSeat ?? (state.gameExt as any)?.shotTheMoonSeat;
    if (authoritativeMoonSeat !== undefined && authoritativeMoonSeat !== null && authoritativeMoonSeat >= 0) {
      moonThreatSeat = Number(authoritativeMoonSeat) as SeatPosition;
    } else {
      for (let s = 0; s < 4; s++) {
        if ((seatRoundPenaltyPoints[s] ?? 0) >= 15) {
          moonThreatSeat = s as SeatPosition;
          break;
        }
      }
    }

    return {
      trickWinnerSeat: (state.trickWinnerSeat !== undefined && state.trickWinnerSeat !== null)
        ? (Number(state.trickWinnerSeat) as SeatPosition)
        : null,
      isGameOver: Boolean(state.phase === 'GAME_OVER' || (state as any).isGameOver),
      matchWinner: state.matchWinner != null ? Number(state.matchWinner) : null,
      leadSuit: state.currentTrick && state.currentTrick.length > 0 && state.currentTrick[0]?.card
        ? state.currentTrick[0].card.suit
        : null,
      currentTurnSeat: (state.currentTurnSeat !== undefined && state.currentTurnSeat !== null)
        ? (Number(state.currentTurnSeat) as SeatPosition)
        : null,
      hearts: {
        heartsBroken: Boolean(state.heartsBroken ?? (state.gameExt as any)?.heartsBroken),
        queenOfSpadesPlayed,
        queenOfSpadesTakenBySeat,
        jackOfDiamondsPlayed: Boolean(state.jackOfDiamondsPlayed ?? (state.gameExt as any)?.jackOfDiamondsPlayed),
        jackOfDiamondsTakenBySeat: (state.jackOfDiamondsTakenBySeat !== undefined && state.jackOfDiamondsTakenBySeat !== null)
          ? (Number(state.jackOfDiamondsTakenBySeat) as SeatPosition)
          : ((state.gameExt as any)?.jackOfDiamondsTakenBySeat !== undefined && (state.gameExt as any)?.jackOfDiamondsTakenBySeat !== null)
            ? (Number((state.gameExt as any).jackOfDiamondsTakenBySeat) as SeatPosition)
            : null,
        shootTheMoonChoice: (state.shootTheMoonChoice ?? (state.gameExt as any)?.shootTheMoonChoice) as any,
        moonChoicePending: Boolean(state.moonChoicePending ?? (state.gameExt as any)?.moonChoicePending),
        passingDirection: (state.passingDirection ?? (state.gameExt as any)?.passingDirection ?? 'LEFT') as any,
        playerScores,
        seatRoundPenaltyPoints,
        isMoonShootingThreatSeat: moonThreatSeat
      }
    };
  }

  public getPresentation(
    state: ArenaGameState,
    config?: GameTableConfig | null
  ): GamePresentationResult {
    const indicators = this.getDisplayIndicators(state, config);
    const contractSummary = this.formatContract(state, config);
    const outcomeText = this.formatOutcome(state, config);

    return {
      ...this.presentation,
      contractSummary,
      outcomeText,
      indicators
    };
  }

  public formatContract(state: ArenaGameState, config?: GameTableConfig | null): string {
    const dir = state.passingDirection || (state.gameExt as any)?.passingDirection || 'LEFT';
    switch (dir) {
      case 'LEFT': return 'Pass 3 Left';
      case 'RIGHT': return 'Pass 3 Right';
      case 'ACROSS': return 'Pass 3 Across';
      case 'HOLD':
      case 'NONE': return 'Hold (No Pass)';
      default: return `Pass ${dir}`;
    }
  }

  public formatOutcome(state: ArenaGameState, config?: GameTableConfig | null, context?: any): string {
    return this.contractFormatter.formatContractOutcome(state, context, config ?? undefined);
  }

  public mapRealtimeState(raw: unknown): Record<string, unknown> {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      return {};
    }
    const data = raw as Record<string, unknown>;

    const playerScores: number[] = Array.isArray(data.playerScores)
      ? data.playerScores.filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
      : [0, 0, 0, 0];

    const seatRoundPenaltyPoints: number[] = Array.isArray(data.seatRoundPenaltyPoints)
      ? data.seatRoundPenaltyPoints.filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
      : (Array.isArray(data.seatPoints)
        ? data.seatPoints.filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
        : [0, 0, 0, 0]);

    return {
      heartsBroken: typeof data.heartsBroken === 'boolean' ? data.heartsBroken : false,
      queenOfSpadesPlayed: typeof data.queenOfSpadesPlayed === 'boolean' ? data.queenOfSpadesPlayed : false,
      queenOfSpadesTakenBySeat: typeof data.queenOfSpadesTakenBySeat === 'number' && Number.isFinite(data.queenOfSpadesTakenBySeat)
        ? data.queenOfSpadesTakenBySeat
        : null,
      jackOfDiamondsPlayed: typeof data.jackOfDiamondsPlayed === 'boolean' ? data.jackOfDiamondsPlayed : false,
      jackOfDiamondsTakenBySeat: typeof data.jackOfDiamondsTakenBySeat === 'number' && Number.isFinite(data.jackOfDiamondsTakenBySeat)
        ? data.jackOfDiamondsTakenBySeat
        : null,
      shootTheMoonChoice: typeof data.shootTheMoonChoice === 'string' ? data.shootTheMoonChoice : null,
      shootTheMoonPoints: typeof data.shootTheMoonPoints === 'number' ? data.shootTheMoonPoints : 26,
      moonChoicePending: typeof data.moonChoicePending === 'boolean' ? data.moonChoicePending : false,
      jackOfDiamonds: typeof data.jackOfDiamonds === 'boolean' ? data.jackOfDiamonds : false,
      shootTheMoonRule: typeof data.shootTheMoonRule === 'string' ? data.shootTheMoonRule : 'CHOICE',
      passingDirection: typeof data.passingDirection === 'string' ? data.passingDirection : 'LEFT',
      playerScores,
      seatRoundPenaltyPoints,
      shotTheMoonSeat: typeof data.shotTheMoonSeat === 'number' && Number.isFinite(data.shotTheMoonSeat)
        ? data.shotTheMoonSeat
        : null,
      passesSubmitted: Array.isArray(data.passesSubmitted)
        ? data.passesSubmitted.filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
        : [],
    };
  }

  public getBotTurn(state: ArenaGameState): BotTurnResolution | null {
    if (!state) return null;
    const phase = state.phase;

    // In Hearts, passing is simultaneous player input; seated bots are resolved atomically on human submit
    if (phase === 'PASSING') {
      return null;
    }

    if (phase !== 'PLAYING') {
      return null;
    }

    const seatsList = extractNormalizedSeatsList(state.seats);
    if (state.currentTurnSeat === null || state.currentTurnSeat === undefined) {
      return null;
    }

    const activeSeat = Number(state.currentTurnSeat) as SeatPosition;
    if (!seatsList?.[Number(activeSeat)]?.isBot) {
      return null;
    }

    const turnKey = `${state.handNumber}_${phase}_${activeSeat}_${state.completedTricks?.length || 0}_${state.currentTrick?.length || 0}`;

    return {
      activeSeat,
      phase,
      turnKey,
      actionType: 'PLAY'
    };
  }

  public getBotTurnDelayMs(state: ArenaGameState, context?: BotTurnTimingContext): number {
    const timing = context?.config?.timing ?? DEFAULT_TIMING;
    const phase = state.phase;

    if (phase === 'PASSING') {
      return 500;
    }
    if (phase === 'PLAYING') {
      const completedCount = state.completedTricks?.length || 0;
      const isLeadingNewTrick = (state.currentTrick?.length || 0) === 0 || (state.currentTrick?.length || 0) >= 4;
      const isLeadingAfterCompletedTrick = isLeadingNewTrick && completedCount > 0;

      if (isLeadingAfterCompletedTrick) {
        const timeSinceTrickEnd = Date.now() - (context?.lastTrickEndTime || 0);
        const trickEndGracePeriod = Math.max(0, 960 - timeSinceTrickEnd);
        return Math.max(timing.botPlayDelayMs, 360, trickEndGracePeriod);
      }

      if (isLeadingNewTrick) {
        return Math.min(timing.botPlayDelayMs, 360);
      }

      // Mid-trick plays: steady metronome cadence measured from when the previous card landed
      const timeSinceLastCard = context?.lastCardPlayTime ? Math.max(0, Date.now() - context.lastCardPlayTime) : 0;
      const targetDispatchCadence = 200;
      return Math.max(50, targetDispatchCadence - timeSinceLastCard);
    }
    return timing.botPlayDelayMs;
  }

  public getLastActorSeat(state: ArenaGameState): SeatPosition | null {
    const comp = state.completedTricks || [];
    const latestComp = comp.length > 0 ? comp[comp.length - 1] : null;
    const trickSeat = (state.currentTrick && state.currentTrick.length > 0)
      ? Number(state.currentTrick[state.currentTrick.length - 1].seat)
      : (latestComp?.winnerSeat !== null && latestComp?.winnerSeat !== undefined)
        ? Number(latestComp.winnerSeat)
        : (latestComp?.winningSeat !== null && latestComp?.winningSeat !== undefined)
          ? Number(latestComp.winningSeat)
          : (state.trickWinnerSeat !== null && state.trickWinnerSeat !== undefined)
            ? Number(state.trickWinnerSeat)
            : null;

    return trickSeat !== null && !isNaN(trickSeat) ? (trickSeat as SeatPosition) : null;
  }
}

export const heartsModule = new HeartsGameModule();
